import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import BulkService from "../../service/bulkService";
import { GraphQLClient } from "../../../../../shared/gql-client";

const bulkAPI = new BulkService();

interface SelectionState {
  pageSelection: Array<{ page: string; ids: string[] }>;
  bulkValues: Array<{ field: string; value: string }>;
  // Track dealers for selected items: { dealerId: { tireSetIds: string[], dealerName: string } }
  selectedItemsByDealer: Record<
    string,
    { tireSetIds: string[]; dealerName: string }
  >;
  // Track items without dealers
  selectedItemsWithoutDealer: string[];
}

const initialState: SelectionState = {
  pageSelection: [],
  bulkValues: [],
  selectedItemsByDealer: {},
  selectedItemsWithoutDealer: [],
};

export const bulkUpdate = createAsyncThunk(
  "bulkUpdate",
  async (
    {
      model,
      ids,
      fields,
      bulkFieldsResponseExclude,
    }: {
      model: string;
      ids: string[];
      fields: Array<{ field: string; value: string }>;
      bulkFieldsResponseExclude?: string[];
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    bulkAPI.setClient(client);

    try {
      return await bulkAPI.mutate(
        model,
        ids,
        fields,
        bulkFieldsResponseExclude,
      );
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const bulkSelectSlice = createSlice({
  name: "bulkSelect",
  initialState: initialState,
  reducers: {
    clearSelectedIds(state, action: PayloadAction<{ page: string }>) {
      const { page } = action.payload;

      // Get the IDs being cleared from this page
      const pageEntry = state.pageSelection.find(
        (entry) => entry.page === page,
      );
      const idsBeingCleared = pageEntry?.ids || [];

      // Remove the page from selection
      state.pageSelection = state.pageSelection.filter(
        (entry) => entry.page !== page,
      );

      // Only clear dealer tracking for the IDs from this specific page
      for (const dealerId in state.selectedItemsByDealer) {
        const dealerData = state.selectedItemsByDealer[dealerId];
        dealerData.tireSetIds = dealerData.tireSetIds.filter(
          (id) => !idsBeingCleared.includes(id),
        );
        // Clean up empty dealer entries
        if (dealerData.tireSetIds.length === 0) {
          delete state.selectedItemsByDealer[dealerId];
        }
      }

      // Only clear items without dealers that belong to this page
      state.selectedItemsWithoutDealer =
        state.selectedItemsWithoutDealer.filter(
          (id) => !idsBeingCleared.includes(id),
        );
    },
    addSelectedId: (
      state,
      action: PayloadAction<{
        id: string;
        page: string;
        dealerInfo?: { id: string; name: string } | null;
      }>,
    ) => {
      const { id, page, dealerInfo } = action.payload;

      // Update page selection
      if (!state.pageSelection.find((entry) => entry.page === page)) {
        state.pageSelection.push({ page, ids: [id] });
      } else {
        const entry = state.pageSelection.find((entry) => entry.page === page);
        if (entry) {
          if (!entry.ids.includes(id)) {
            entry.ids.push(id);
          }
        }
      }

      // Remove id from all dealers before adding to the new one
      for (const dealerId in state.selectedItemsByDealer) {
        const dealerData = state.selectedItemsByDealer[dealerId];
        const itemIndex = dealerData.tireSetIds.indexOf(id);
        if (itemIndex > -1) {
          dealerData.tireSetIds.splice(itemIndex, 1);
          // Clean up empty dealer entries
          if (dealerData.tireSetIds.length === 0) {
            delete state.selectedItemsByDealer[dealerId];
          }
        }
      }

      // Track dealer information
      if (dealerInfo) {
        if (!state.selectedItemsByDealer[dealerInfo.id]) {
          state.selectedItemsByDealer[dealerInfo.id] = {
            tireSetIds: [],
            dealerName: dealerInfo.name,
          };
        }
        if (
          !state.selectedItemsByDealer[dealerInfo.id].tireSetIds.includes(id)
        ) {
          state.selectedItemsByDealer[dealerInfo.id].tireSetIds.push(id);
        }
        // Remove from no-dealer list if it was there
        state.selectedItemsWithoutDealer =
          state.selectedItemsWithoutDealer.filter((itemId) => itemId !== id);
      } else {
        // Track items without dealers
        if (!state.selectedItemsWithoutDealer.includes(id)) {
          state.selectedItemsWithoutDealer.push(id);
        }
      }
    },
    removeAllIds: (
      state,
      action: PayloadAction<{ ids: string[]; page: string }>,
    ) => {
      const { ids, page } = action.payload;
      const pageEntry = state.pageSelection.find(
        (entry) => entry.page === page,
      );

      if (pageEntry) {
        pageEntry.ids = pageEntry.ids.filter((id) => !ids.includes(id));
      }

      // Remove from dealer tracking
      for (const dealerId in state.selectedItemsByDealer) {
        const dealerData = state.selectedItemsByDealer[dealerId];
        dealerData.tireSetIds = dealerData.tireSetIds.filter(
          (id) => !ids.includes(id),
        );
        // Clean up empty dealer entries
        if (dealerData.tireSetIds.length === 0) {
          delete state.selectedItemsByDealer[dealerId];
        }
      }

      // Remove from no-dealer tracking
      state.selectedItemsWithoutDealer =
        state.selectedItemsWithoutDealer.filter((id) => !ids.includes(id));
    },
    addAllIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        page: string;
        dealerInfos?: Array<{
          id: string;
          dealerInfo: { id: string; name: string } | null;
        }>;
      }>,
    ) => {
      const { ids, page, dealerInfos } = action.payload;
      const pageEntry = state.pageSelection.find(
        (entry) => entry.page === page,
      );

      if (!pageEntry) {
        state.pageSelection.push({ page, ids });
      } else {
        pageEntry.ids = Array.from(new Set([...pageEntry.ids, ...ids]));
      }

      // Track dealer information for each item if provided
      if (dealerInfos) {
        for (const item of dealerInfos) {
          const { id, dealerInfo } = item;

          // Remove id from all dealers before adding to the new one
          for (const dealerId in state.selectedItemsByDealer) {
            const dealerData = state.selectedItemsByDealer[dealerId];
            const itemIndex = dealerData.tireSetIds.indexOf(id);
            if (itemIndex > -1) {
              dealerData.tireSetIds.splice(itemIndex, 1);
              // Clean up empty dealer entries
              if (dealerData.tireSetIds.length === 0) {
                delete state.selectedItemsByDealer[dealerId];
              }
            }
          }

          if (dealerInfo) {
            if (!state.selectedItemsByDealer[dealerInfo.id]) {
              state.selectedItemsByDealer[dealerInfo.id] = {
                tireSetIds: [],
                dealerName: dealerInfo.name,
              };
            }
            if (
              !state.selectedItemsByDealer[dealerInfo.id].tireSetIds.includes(
                id,
              )
            ) {
              state.selectedItemsByDealer[dealerInfo.id].tireSetIds.push(id);
            }
            // Remove from no-dealer list if it was there
            state.selectedItemsWithoutDealer =
              state.selectedItemsWithoutDealer.filter(
                (itemId) => itemId !== id,
              );
          } else {
            // Track items without dealers
            if (!state.selectedItemsWithoutDealer.includes(id)) {
              state.selectedItemsWithoutDealer.push(id);
            }
          }
        }
      }
    },
    removeSelectedId: (
      state,
      action: PayloadAction<{ id: string; page: string }>,
    ) => {
      const { id, page } = action.payload;

      // Remove from page selection
      const entry = state.pageSelection.find((entry) => entry.page === page);
      if (entry) {
        const index = entry.ids.indexOf(id);
        if (index > -1) {
          entry.ids.splice(index, 1);
        }
      }

      // Remove from dealer tracking
      for (const dealerId in state.selectedItemsByDealer) {
        const dealerData = state.selectedItemsByDealer[dealerId];
        const itemIndex = dealerData.tireSetIds.indexOf(id);
        if (itemIndex > -1) {
          dealerData.tireSetIds.splice(itemIndex, 1);
          // Clean up empty dealer entries
          if (dealerData.tireSetIds.length === 0) {
            delete state.selectedItemsByDealer[dealerId];
          }
          break;
        }
      }

      // Remove from no-dealer tracking
      state.selectedItemsWithoutDealer =
        state.selectedItemsWithoutDealer.filter((itemId) => itemId !== id);
    },
    updateBulkValues: (
      state,
      action: PayloadAction<Array<{ field: string; value: string }>>,
    ) => {
      state.bulkValues = action.payload;
    },
    resetBulkValues: (state) => {
      state.bulkValues = [];
    },
  },
});

export const {
  addSelectedId,
  removeSelectedId,
  addAllIds,
  removeAllIds,
  clearSelectedIds,
  updateBulkValues,
  resetBulkValues,
} = bulkSelectSlice.actions;
export default bulkSelectSlice.reducer;
